import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { CheckCircle } from '@phosphor-icons/react';

const SubscriptionSuccess = ({ history, location, store }) => {
  // Get subscription details from location state
  const { organizationName, subscriptionId, paymentSuccess } =
    location.state || {};
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [redirectTimer, setRedirectTimer] = useState(8);

  // Refresh subscription status and redirect to dashboard
  useEffect(() => {
    const refreshStatus = async () => {
      if (!organizationName || !store.isLoggedIn) {
        // If missing info or not logged in, redirect to login
        history.push('/login');
        return;
      }

      try {
        setIsRefreshing(true);
        setStatusMessage('Actualizando estado de la suscripción...');

        // Call our new method to refresh subscription status
        const result = await store.refreshSubscriptionStatus();
        console.log('Subscription status refresh result:', result);

        if (result.success) {
          setStatusMessage(
            '¡Suscripción activada correctamente! Redirigiendo al dashboard...'
          );

          // Start countdown for UX purposes
          const timerInterval = setInterval(() => {
            setRedirectTimer(prev => {
              if (prev <= 1) {
                clearInterval(timerInterval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          // Set a timeout to redirect to dashboard
          const timer = setTimeout(() => {
            store.redirect = '/'; // Use store.redirect to ensure proper navigation
            history.push('/'); // Also directly push to dashboard as a fallback
          }, 5000);

          return () => {
            clearTimeout(timer);
            clearInterval(timerInterval);
          };
        } else {
          // If refresh was not successful, show message and redirect to login after delay
          setStatusMessage(
            result.message ||
              'Error al activar suscripción. Intentando de nuevo...'
          );
          setTimeout(() => {
            history.push('/login');
          }, 5000);
        }
      } catch (error) {
        console.error('Error refreshing subscription:', error);
        setStatusMessage(
          'Error al actualizar el estado. Iniciando sesión de nuevo...'
        );
        setTimeout(() => {
          history.push('/login');
        }, 5000);
      } finally {
        setIsRefreshing(false);
      }
    };

    // Only run if we have a successful payment
    if (paymentSuccess) {
      refreshStatus();
    } else if (organizationName) {
      // Legacy path for old code - redirect to login
      const timer = setTimeout(() => {
        history.push('/login');
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [organizationName, subscriptionId, paymentSuccess, store, history]);

  if (!organizationName) {
    // If we don't have organization info, redirect to login
    return (
      <div className="flex flex-col items-center justify-center p-5 h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600 mb-4"></div>
        <p className="text-gray-600">Redirigiendo al inicio de sesión...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="w-full p-8 overflow-y-auto flex items-center justify-center flex-1">
        <div className="w-full md:max-w-md 2xl:max-w-xl">
          <div className="bg-white rounded-xl shadow-xl transform transition-all shadow-md hover:shadow-2xl focus:shadow-2xl">
            <div className="px-4 py-4 md:px-12 md:py-12">
              <div className="text-center mb-6">
                <CheckCircle
                  size={64}
                  weight="fill"
                  className="text-green-500 mx-auto"
                />
                <h2 className="text-3xl font-medium text-gray-900 mt-4">
                  ¡Suscripción activada!
                </h2>
              </div>

              <p className="text-gray-600 mb-6">
                Tu suscripción a <strong>{organizationName}</strong> ha sido
                activada correctamente. Ahora tienes acceso completo a todos los
                materiales y funciones del curso.
              </p>

              <div className="mb-8 p-4 bg-gray-50 rounded-md border border-gray-200">
                <h3 className="font-medium mb-2">
                  Detalles de la suscripción:
                </h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Tu suscripción está ahora activa</li>
                  <li>• Puedes gestionar tu suscripción desde tu perfil</li>
                  <li>
                    • Se te facturará mensualmente y puedes cancelar en
                    cualquier momento
                  </li>
                </ul>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={() => (store.redirect = '/')}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  disabled={isRefreshing}
                >
                  Ir al dashboard
                </button>

                <button
                  onClick={() => history.push('/student/subscriptions/status')}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={isRefreshing}
                >
                  Ver suscripción
                </button>
              </div>

              <p className="text-sm text-gray-500 text-center mt-6">
                {statusMessage ||
                  (isRefreshing
                    ? 'Actualizando estado de la suscripción...'
                    : `Serás redirigido al dashboard en ${redirectTimer} segundos...`)}
              </p>

              {isRefreshing && (
                <div className="flex justify-center mt-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default inject('store')(observer(withRouter(SubscriptionSuccess)));
